import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ICallToActionProps } from '../../components/common/blocks/CallToAction';
import { IExpertTipProps } from '../../components/common/blocks/ExpertTip';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import TeamMemberBox, {
  ITeamMemberBoxProps,
} from '../../components/team-members/TeamMemberBox';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICustomerReviewsMeta,
  IExpertTipComponent,
  IFaqLandingPage,
  ILayoutPageProps,
  ILinkWithIconComponent,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateFaqLandingPageSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
  removeMarkDown,
  removeObjectProperties,
} from '../../shared/useUtils';
import { FaqLandingStyle } from '../../styles/pages/faq-landing';
import { NextPageWithLayout } from '../_app';

interface IFaqLandingPageProps extends ILayoutPageProps {
  title: string;
  summary: string;
  body: string;
  formSuccessMessage: string;
  formErrorMessage: string;
  contactMethods: IContactMethod[];
  contactUsForm: IContactUsForm;
  callToAction: ICallToActionComponent;
  expertTip: IExpertTipComponent;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  pageSchema: PageJSONLDSchema;
  lastUpdatedLabel: string;
  lastUpdatedDate: Date;
  imageUrl: string;
  tableOfContentsTitle: string;
  author: {
    fullname: string;
    jobTitle: string;
    biography: string;
    socialMediaIcons: ILinkWithIconComponent[];
    avatarUrl: string;
    slug: string;
  };
}

const FaqLandingPage: NextPageWithLayout<IFaqLandingPageProps> = (
  props: IFaqLandingPageProps
) => {
  const router = useRouter();

  const scrollToHeadingByText = (text: string) => {
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const normalizedText = text.trim().toLowerCase();
    const navBar = document.querySelector('[class^="Navbar_"]');

    let offset = 0;
    if (navBar) {
      offset = (navBar as HTMLElement).offsetHeight;
    } else {
      offset = 144;
    }

    for (const heading of Array.from(headings)) {
      if ((heading.textContent || '').trim().toLowerCase() === normalizedText) {
        const rect = heading.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const top = rect.top + scrollTop - offset - 20;
        window.scrollTo({ top, behavior: 'smooth' });
        break;
      }
    }
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.formSuccessMessage,
      submitErrorMessage: props.formErrorMessage,
    },
  };

  const expertTipProps: IExpertTipProps = {
    title: props.expertTip.title,
    content: props.expertTip.content,
    primaryButton: {
      label: props.expertTip.primaryButtonLabel,
    },
    secondaryButton: {
      label: props.expertTip.secondaryButtonLabel,
      path: props.expertTip.secondaryButtonPath,
    },
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const DynamicContact = dynamic(
    () => import('../../components/contact-us/Contact'),
    {
      ssr: false,
    }
  );
  const ExpertTipComponent = dynamic(
    () => import('../../components/common/blocks/ExpertTip')
  );
  const CallToActionComponent = dynamic(
    () => import('../../components/common/blocks/CallToAction')
  );

  interface TableOfContentsItem {
    text: string;
    level: number;
    id: string;
    startsWithNumber: boolean;
  }

  const generateTableOfContents = (body: string): TableOfContentsItem[] => {
    const lines = body.split('\n');
    const headings: TableOfContentsItem[] = [];

    lines.forEach((line) => {
      const match = line.match(/^(#{1,2})\s+(.*)/); // # (h1) and ## (h2) only
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]/g, '');
        const startsWithNumber = /^\d/.test(text);
        headings.push({ text, level, id, startsWithNumber });
      }
    });

    return headings;
  };

  const mapSocialMediaIcons = (icons: ILinkWithIconComponent[]) => {
    return icons.map((socialMediaIcon) => ({
      iconUrl:
        getImageSrcWithFallback(
          flatStrapiObject(socialMediaIcon.icon),
          'full'
        ) || '/assets/images/home/map-image.png',
      link: socialMediaIcon.link,
    }));
  };

  const teamMemberBoxProps: ITeamMemberBoxProps = {
    fullname: props.author.fullname,
    jobTitle: props.author.jobTitle,
    biography: props.author.biography,
    socialMediaIcons: mapSocialMediaIcons(props.author.socialMediaIcons || []),
    imageUrl: props.author.avatarUrl || '',
    slug: props.author.slug,
  };

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <article>
        <div className="page-content-section is-narrow">
          <div className="page-content-block">
            <header className={FaqLandingStyle['faq-landing-header']}>
              <div className={FaqLandingStyle['faq-landing-header-image']}>
                <Image
                  quality={100}
                  fill
                  sizes="(max-width: 768px) 100vw, 2000px"
                  priority={true}
                  alt={props.meta.title}
                  src={
                    props.imageUrl ||
                    '/assets/images/blog/blog-placeholder-image.png'
                  }
                />
              </div>

              <div className={FaqLandingStyle['faq-meta']}>
                <div className={FaqLandingStyle['faq-metadata']}>
                  <p>
                    <a
                      href={`${props.author.slug}`}
                      className={FaqLandingStyle['faq-metadata-author']}>
                      {props.author?.fullname || 'iGerent'}
                    </a>
                    <time
                      className={FaqLandingStyle['faq-metadata-date']}
                      dateTime={formatDate(props.lastUpdatedDate, true)}>
                      {props.lastUpdatedLabel}{' '}
                      {formatDate(props.lastUpdatedDate)}
                    </time>
                  </p>
                </div>
              </div>

              {!!props.title && props.title !== 'n/a' && (
                <div className="page-content-block">
                  <hr className="separator-extra-thin" />
                  <h1>{props.title}</h1>
                  <hr className="separator-extra-thin" />
                </div>
              )}

              {!!props.summary && props.summary !== 'n/a' && (
                <div className="page-content-block is-boxed">
                  <ReactMarkdown>{props.summary}</ReactMarkdown>
                </div>
              )}
            </header>

            <div className={FaqLandingStyle['faq-landing-table-of-contents']}>
              {props.tableOfContentsTitle && (
                <p
                  className={
                    FaqLandingStyle['faq-landing-table-of-contents-title']
                  }>
                  {props.tableOfContentsTitle}
                </p>
              )}
              <ul>
                {generateTableOfContents(props.body).map((item, index) => (
                  <li
                    key={index}
                    className={`${
                      item.startsWithNumber
                        ? FaqLandingStyle[
                            'faq-landing-table-of-contents-item-padded'
                          ]
                        : ''
                    }`}>
                    <span onClick={() => scrollToHeadingByText(item.text)}>
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {!!props.body && props.body !== 'n/a' && (
          <section className="page-content-section is-narrow">
            <div
              className={`page-content-block is-text-align-justified-mobile ${FaqLandingStyle['faq-landing-content']}`}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ node, ...props }) => (
                    <div
                      className={FaqLandingStyle['faq-landing-content-table']}>
                      <table {...props} />
                    </div>
                  ),
                }}>
                {props.body}
              </ReactMarkdown>
            </div>
          </section>
        )}
      </article>

      <div className="page-content-section is-narrow">
        <div className="page-content-block">
          <ExpertTipComponent {...expertTipProps} />
        </div>
      </div>

      <div className="page-content-section is-narrow">
        <div className="page-content-block">
          <TeamMemberBox {...teamMemberBoxProps} />
        </div>
      </div>

      {/* Dynamic loaded from this point */}
      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact">
          <DynamicContact {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToActionComponent {...callToActionProps} />
        </div>
      </div>
    </div>
  );
};

FaqLandingPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

const entityBaseName = 'faq';

export async function getStaticPaths(context: any) {
  let paths: any[] = [];
  const landingsUrlParams = new URLSearchParams({
    'populate[0]': 'metas',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const landings: IFaqLandingPage[] = flatStrapiCollection(
    await fetchStrapi<any>(
      `/${entityBaseName}-landings`,
      landingsUrlParams,
      buildApiKey
    )
  );

  for (const locale of context.locales) {
    const localizedPaths = landings.map((item: IFaqLandingPage) => {
      const localizedMeta = getTranslation(item.metas, locale);
      return {
        params: {
          slug: localizedMeta.slug,
        },
        locale: locale,
      };
    });
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: IFaqLandingPageProps }> {
  const appRoutes = await getRoutes();
  const paramSlug = context.params.slug;

  const landingUrlParams = new URLSearchParams({
    'filters[metas][slug][$eq]': paramSlug,
    'populate[0]': 'metas',
    'populate[1]': 'faqCategory',
    'populate[2]': 'image',
    'populate[3]': 'image.image',
    'populate[4]': 'title',
    'populate[5]': 'summary',
    'populate[6]': 'body',
    'populate[7]': 'expertTip',
    'populate[8]': 'contactUsForm',
    'populate[9]': 'callToAction',
    'populate[10]': 'callToAction.buttons',
    'populate[11]': 'breadcrumbAncestors',
    'populate[12]': 'author',
    'populate[13]': 'author.avatar',
    'populate[14]': 'author.jobTitle',
    'populate[15]': 'author.socialMediaIcons',
    'populate[16]': 'author.socialMediaIcons.icon',
    'populate[17]': 'author.biography',
    'populate[18]': 'author.meta',
    'populate[19]': 'lastUpdatedLabel',
    'populate[20]': 'tableOfContentsTitle',
  });

  const landingsResult = await fetchStrapi<any>(
    `/${entityBaseName}-landings`,
    landingUrlParams,
    buildApiKey
  );
  const landing: IFaqLandingPage =
    flatStrapiCollection<IFaqLandingPage>(landingsResult)[0];
  landing.faqCategory = flatStrapiObject(landing.faqCategory as any);
  if (landing.author) {
    landing.author = flatStrapiObject(landing.author as any);
  }

  if (landing.image && landing.image.length > 0) {
    landing.image = landing.image.map((x) => ({
      ...x,
      image: flatStrapiObject(x.image as any),
    }));
  }

  const title = getTranslation(landing.title, context.locale).text as string;
  const summary = getTranslation(landing.summary, context.locale)
    .text as string;

  const contactMethods = (await getContactMethods()).map((x) =>
    removeObjectProperties(x, ['id', 'createdAt', 'updatedAt'])
  );
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  navBarProps.items = removeObjectProperties(navBarProps.items, [
    'id',
    'createdAt',
    'updatedAt',
  ]);

  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = removeObjectProperties(
    await populateFooterProps(context.locale),
    ['id', 'createdAt', 'updatedAt']
  );

  const pageMeta = getTranslation(landing.metas, context.locale);
  const originalPageMeta = getTranslation(landing.metas, 'en');
  const alternativeLinks = getAlternateLinks(
    `/faqs/${originalPageMeta.slug}`,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', pageMeta.slug);
  const commonTranslations = await getCommonTranslations();
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;

  // Generate JSON-LD schema
  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const contactFormTitle = removeMarkDown(
    getTranslation(landing.contactUsForm, context.locale).title as string
  );
  const callToActionTitle = removeMarkDown(
    getTranslation(landing.callToAction, context.locale).title as string
  );

  // Prepare author for schema
  const schemaAuthor =
    landing.author && landing.author.meta
      ? {
          fullname: landing.author.fullname || '',
          jobTitle: getTranslation(landing.author.jobTitle, context.locale)
            .text as string,
          profileSlug: getTranslation(landing.author.meta, context.locale).slug,
        }
      : null;

  const faqsLandingPageSchema = generateFaqLandingPageSchema(
    title,
    removeMarkDown(summary),
    schemaAuthor,
    contactFormTitle,
    callToActionTitle,
    (landing as any).createdAt,
    (landing as any).updatedAt,
    getCanonicalLink(context.locale, pageMeta.slug)
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...faqsLandingPageSchema],
  };

  // Populate breadcrumbs
  if (landing.breadcrumbAncestors && landing.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = landing.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = getTranslation(
    landing.title,
    context.locale
  ).text as string;

  const formSuccessMessage = isUndefinedOrEmpty(
    landing.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(landing.contactUsFormSuccessMessage, context.locale)
        .text as string);

  const formErrorMessage = isUndefinedOrEmpty(landing.contactUsFormErrorMessage)
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(landing.contactUsFormErrorMessage, context.locale)
        .text as string);

  const tableOfContentsTitle = isUndefinedOrEmpty(landing.tableOfContentsTitle)
    ? (getTranslation(commonTranslations.tableOfContentsTitle, context.locale)
        .text as string)
    : (getTranslation(landing.tableOfContentsTitle, context.locale)
        .text as string);

  const lastUpdatedLabel = isUndefinedOrEmpty(landing.lastUpdatedLabel)
    ? (getTranslation(commonTranslations.lastUpdatedLabel, context.locale)
        .text as string)
    : (getTranslation(landing.lastUpdatedLabel, context.locale).text as string);

  const author = {
    fullname: landing.author?.fullname || '',
    jobTitle: getTranslation(landing.author?.jobTitle, context.locale)
      .text as string,
    biography: getTranslation(landing.author?.biography, context.locale)
      .text as string,
    socialMediaIcons: landing.author?.socialMediaIcons || [],
    avatarUrl:
      getImageSrcWithFallback(
        flatStrapiObject(landing.author?.avatar),
        'full'
      ) || '/assets/images/home/map-image.png',
    slug: getTranslation(landing.author?.meta, context.locale).slug || '',
  };

  const props: IFaqLandingPageProps = {
    // Layout
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta: pageMeta,
    title,
    body: getTranslation(landing.body, context.locale).text as string,
    contactMethods,
    formSuccessMessage,
    formErrorMessage,
    contactUsForm: getTranslation(landing.contactUsForm, context.locale),
    alternativeLinks,
    canonicalLink,
    expertTip: getTranslation(landing.expertTip, context.locale),
    callToAction: getTranslation(landing.callToAction, context.locale),
    summary,
    pageSchema,
    lastUpdatedLabel,
    lastUpdatedDate: landing.updatedAt || new Date(),
    imageUrl: landing.image
      ? getImageSrcWithFallback(
          getTranslation(landing.image, context.locale).image,
          'full'
        )
      : '',
    tableOfContentsTitle,
    author,
  };

  return {
    props,
  };
}

export default FaqLandingPage;
